/**
 * CPT / J-code submission-status table: headers, column keys, and row factory.
 *
 * @module cptCodesTable
 */
import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  AuthOnFile,
  JCodeDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { TableCellWithColoredText } from "../../../../data-model/tableCells";
import { getAuthStatusOfOneJCode } from "../../../../utils/getAuthStatusOfMedicalPaOrder";

/** Keys for columns in the submission-status CPT codes table. */
export enum CptCodesTableColumnKeys {
  CPT_CODE = "cptCode",
  CPT_DESCRIPTION = "cptDescription",
  STATUS = "authStatus",
  START_DATE = "startDate",
}

/** One rendered row keyed by {@link CptCodesTableColumnKeys}. */
export interface CptCodeTabledata {
  [CptCodesTableColumnKeys.CPT_CODE]: string;
  [CptCodesTableColumnKeys.CPT_DESCRIPTION]: string;
  [CptCodesTableColumnKeys.STATUS]: TableCellWithColoredText;
  [CptCodesTableColumnKeys.START_DATE]: string;
}

/** Custom-table header definitions for the submission CPT codes grid. */
export const cptTableHeader: TableHeader[] = [
  {
    label: "CPT Code",
    key: CptCodesTableColumnKeys.CPT_CODE,
    width: 20,
    order: 1,
    type: TableCellType.STRING,
    sortable: true,
  },
  {
    label: "Description",
    key: CptCodesTableColumnKeys.CPT_DESCRIPTION,
    width: 60,
    order: 2,
    type: TableCellType.STRING,
    sortable: true,
  },
  {
    label: "Status",
    key: CptCodesTableColumnKeys.STATUS,
    width: 20,
    order: 3,
    type: TableCellType.BADGE,
    sortable: true,
  },
];

/**
 * Flattens all J-codes from the order's auth entries into badge-decorated table rows.
 *
 * @param authOnFile - Postgres auth-on-file data (`authOnFileDetails.auth_on_file`)
 * @param createdAt - Order creation timestamp used for the Start Date column
 * @param authStatusOptions - Maps line-level auth status ids to badge colors/text
 * @returns Rows for {@link cptTableHeader}; empty array when `authOnFile` is missing
 */
export const createCptCodesTableData = (
  authOnFile?: AuthOnFile,
  createdAt?: string,
  authStatusOptions?: AuthStatusOptionModel[],
) => {
  if (!authOnFile) {
    return [];
  }

  // The Postgres `/order-auth` payload returns the auth ranges under `entries`;
  // `auth_entries` is the legacy fallback. Mirror the single-order CPT table.
  const authEntries = authOnFile?.entries ?? authOnFile?.auth_entries ?? [];
  const getAllJCodesData = () => {
    let JcodesData: JCodeDetails[] = [];
    authEntries.forEach((item) => {
      JcodesData.push(...(item?.j_codes ?? []));
    });
    return JcodesData;
  };
  const JcodesData = getAllJCodesData();
  return JcodesData.map((item) => {
    const authStatus = getAuthStatusOfOneJCode(item, authStatusOptions ?? []);

    return {
      [CptCodesTableColumnKeys.CPT_CODE]: item?.j_code ?? "",
      [CptCodesTableColumnKeys.CPT_DESCRIPTION]:
        item?.medicine_name ?? item?.description ?? "",
      [CptCodesTableColumnKeys.STATUS]: {
        text: authStatus?.text,
        color: authStatus?.textColor,
        bgColor: authStatus?.bgColor,
      },

      [CptCodesTableColumnKeys.START_DATE]: createdAt
        ? moment(createdAt).format("MM/DD/YYYY")
        : "",
    };
  });
};
